/**
 * @license Copyright (c) 2003-2020, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

// The editor creator to use.
import BalloonEditorBase from '@ckeditor/ckeditor5-editor-balloon/src/ballooneditor';
import ClassicEditorBase from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';
import InlineEditorBase from '@ckeditor/ckeditor5-editor-inline/src/inlineeditor';

import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import Font from '@ckeditor/ckeditor5-font/src/font';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import Heading from '@ckeditor/ckeditor5-heading/src/heading';
import Image from '@ckeditor/ckeditor5-image/src/image';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload';
import Indent from '@ckeditor/ckeditor5-indent/src/indent';
import Link from '@ckeditor/ckeditor5-link/src/link';
import List from '@ckeditor/ckeditor5-list/src/list';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice';
import Table from '@ckeditor/ckeditor5-table/src/table';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation';
// import TrackChanges from '@ckeditor/ckeditor5-track-changes/src/trackchanges';
// import Comments from '@ckeditor/ckeditor5-comments/src/comments';
import Autosave from '@ckeditor/ckeditor5-autosave/src/autosave';
import Highlight from '@ckeditor/ckeditor5-highlight/src/highlight';
import WordCount from '@ckeditor/ckeditor5-word-count/src/wordcount';
import MediaEmbed from "@ckeditor/ckeditor5-media-embed/src/mediaembed";
import MediaToolbar from "@ckeditor/ckeditor5-media-embed/src/mediaembedtoolbar";
import MediaStyle from "@kimnagui/ckeditor5-media-align";
import SimpleUploadAdapter from '@ckeditor/ckeditor5-upload/src/adapters/simpleuploadadapter';
import RestrictedEditingMode from '@ckeditor/ckeditor5-restricted-editing/src/restrictededitingmode';
import PendingActions from '@ckeditor/ckeditor5-core/src/pendingactions';
// import CommentsRepository from '@ckeditor/ckeditor5-comments/src/comments/commentsrepository';
import Mention from '@ckeditor/ckeditor5-mention/src/mention';

// Custom plugin to add templated questions
import AddSectionWidget from '../../ckeditor-add-question-widget/src/addquestion';
import AISuggestion from '../../ckeditor-ai-suggestion/src/aisuggestion';

class ClassicEditor extends ClassicEditorBase { }
class BalloonEditor extends BalloonEditorBase { }
class InlineEditor extends InlineEditorBase { }

// Plugins to include in the build.
const builtinPlugins = [
	Essentials,
	Autoformat,
	Bold,
	Italic,
	Underline,
	Alignment,
	Font,
	BlockQuote,
	Heading,
	Image,
	ImageCaption,
	ImageStyle,
	ImageToolbar,
	ImageUpload,
	Indent,
	Link,
	List,
	Paragraph,
	PasteFromOffice,
	Table,
	TableToolbar,
	TextTransformation,
	// TrackChanges,
	// Comments,
	Autosave,
	Highlight,
	WordCount,
	MediaEmbed,
	MediaToolbar,
	MediaStyle,
	SimpleUploadAdapter,
	RestrictedEditingMode,
	PendingActions,
	// CommentsRepository,
	AddSectionWidget,
	AISuggestion,
	Mention,
	MentionCustomization
];

ClassicEditor.builtinPlugins = builtinPlugins;
BalloonEditor.builtinPlugins = builtinPlugins;
InlineEditor.builtinPlugins = builtinPlugins;

// Editor configuration.
const defaultConfig = {
	toolbar: {
		items: [
			'heading',
			'|',
			'bold',
			'italic',
			'underline',
			'|',
			'fontSize',
			'fontFamily',
			'fontColor',
			'fontBackgroundColor',
			'|',
			'alignment',
			'blockQuote',
			'link',
			'bulletedList',
			'numberedList',
			'|',
			'indent',
			'outdent',
			'|',
			'imageUpload',
			// 'uploadImage',
			'insertTable',
			'mediaEmbed',
			'undo',
			'redo',
			'|',
			'highlight',
			// 'comment',
			'restrictedEditing',
			'|',
			'simpleBox',
			'getAI'
		]
	},
	image: {
		toolbar: [
			'imageStyle:block', 'imageStyle:alignLeft', "imageStyle:alignCenter", 'imageStyle:alignRight', '|', 'imageTextAlternative'
		],
		styles: [
			// This option is equal to a situation where no style is applied.
			'full',

			'alignCenter',

			// This represents an image aligned to the left.
			'alignLeft',

			// This represents an image aligned to the right.
			'alignRight'
		]
	},
	mediaEmbed: {
		removeProviders: [ 'instagram', 'twitter', 'googleMaps', 'flickr', 'facebook', 'youtube' ],
		toolbar: [
			"mediaStyle:full",
			"|",
			"mediaStyle:alignLeft",
			"mediaStyle:alignCenter",
			"mediaStyle:alignRight",
		],
		styles: ["full", "alignLeft", "alignCenter", "alignRight"],
		locales: { // optional
			full: "Full size video",
			alignLeft: "Left aligned video",
			alignCenter: "Centered video",
			alignRight: "Right aligned video",
		},
	},
	table: {
		contentToolbar: [
			'tableColumn',
			'tableRow',
			'mergeTableCells'
		],
		tableToolbar: []
	},
	fontFamily: {
		options: [
			'default',
			'Arial, Helvetica, sans-serif',
			'Courier New, Courier, monospace',
			'Georgia, serif',
			'Lucida Sans Unicode, Lucida Grande, sans-serif',
			'Tahoma, Geneva, sans-serif',
			'Times New Roman, Times, serif',
			'Trebuchet MS, Helvetica, sans-serif',
			'Verdana, Geneva, sans-serif',
			'Ubuntu, Arial, sans-serif',
			'Ubuntu Mono, Courier New, Courier, monospace',
			'Poppins',
			'Rubik'
		],
		supportAllValues: true
	},
	// This value must be kept in sync with the language defined in webpack.config.js.
	language: 'en'
};

ClassicEditor.defaultConfig = defaultConfig;
BalloonEditor.defaultConfig = defaultConfig;
InlineEditor.defaultConfig = defaultConfig;

export default {
	ClassicEditor, BalloonEditor, InlineEditor
};

function MentionCustomization(editor) {
	// The upcast converter will convert view <a class="mention" href="" data-user-id="">
	// elements to the model 'mention' text attribute.
	editor.conversion.for('upcast').elementToAttribute({
		view: {
			name: 'span',
			key: 'data-mention',
			classes: 'mention',
			attributes: {
				// href: true,
				'data-user-id': true
			}
		},
		model: {
			key: 'mention',
			value: viewItem => {
				// The mention feature expects that the mention attribute value
				// in the model is a plain object with a set of additional attributes.
				// In order to create a proper object use the toMentionAttribute() helper method:
				const mentionAttribute = editor.plugins.get('Mention').toMentionAttribute(viewItem, {
					// Add any other properties that you need.
					// link: viewItem.getAttribute('href'),
					userId: viewItem.getAttribute('data-user-id')
				});

				return mentionAttribute;
			}
		},
		converterPriority: 'high'
	});

	// Downcast the model 'mention' text attribute to a view <a> element.
	editor.conversion.for('downcast').attributeToElement({
		model: 'mention',
		view: (modelAttributeValue, { writer }) => {
			// Do not convert empty attributes (lack of value means no mention).
			if (!modelAttributeValue) {
				return;
			}

			return writer.createAttributeElement('span', {
				class: 'mention',
				'data-mention': modelAttributeValue.id,
				'data-user-id': modelAttributeValue.userId
				// 'href': modelAttributeValue.link
			}, {
				// Make mention attribute to be wrapped by other attribute elements.
				priority: 20,
				// Prevent merging mentions together.
				id: modelAttributeValue.uid
			});
		},
		converterPriority: 'high'
	});
};